import handleRequestError from "../../http/services/handleRequestError/handleRequestError";
import UsersRepository from "@/core/administration/repositories/UsersRepository";
import {
  createUserLocalUrl,
  getCurrentUserUrl,
  getUsersLocalUrl,
  getUserByIdLocalUrl,
  updateUserLocalUrl,
  toggleActivateUserLocalUrl,
  assignRolesToUsersUrl,
  revokeRolesFromUserUrl,
  searchLdapUserByIdUrl,
  searchUserByLoginUrl,
} from "../../api/usersBackendEndpoints/usersBackendEndpoints";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import {
  executeRequest,
  executeVoidRequest,
} from "../../http/services/executeRequest/executeRequest";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import {
  CreateUserRequest,
  CreateUserResponse,
  CreatedUserApiData,
  UserData,
  UsersResponse,
  UserDetailsResponse,
  AssignRoleToUsersRequest,
  AssignRoleToUsersResponse,
  RevokeRolesFromUserRequest,
  RevokeRolesFromUserResponse,
  LDAPUserData,
  SearchLdapUserResponse,
  SearchUserByLoginResponse,
  UserByLoginData,
} from "@/core/administration/entities/Users";
import { UserDataMapper } from "@/core/administration/mappers/UserDataMappers";
import type { StaffDirectoryAttachment } from "@/core/authUser/entities/StaffDirectoryAttachment";
import {
  type CurrentUserDto,
  mapStaffDirectoryDtoToEntity,
} from "./currentUserMappers";

export default class UsersRepositoryBackend implements UsersRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async getUsers(
    name: string,
    status: string,
    agency: string,
    role: string,
    page: number,
    size: number,
  ): Promise<RepositoryResult<UsersResponse>> {
    try {
      const params = new URLSearchParams();
      params.set("offset", String(page));
      params.set("rows", String(size));

      if (name) params.set("name", name);
      if (status) params.set("status", status);
      if (agency) params.set("agency", agency);
      if (role) params.set("role", role);

      const url = `${getUsersLocalUrl()}?${params.toString()}`;

      const response = await this.httpClient.get<
        ApiResponse<{ items: UserData[]; totalElements: number }>
      >({
        url,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<{
        items: UserData[];
        totalElements: number;
      }>(response);

      if (result.success) {
        const usersResponse: UsersResponse = {
          code: response.code,
          message: response.message,
          data: result.data
            ? {
                items: UserDataMapper.toDomainModel({
                  code: response.code,
                  message: response.message,
                  data: result.data,
                } as UsersResponse),
                totalElements: result.data.totalElements || 0,
              }
            : { items: [], totalElements: 0 },
        };

        return RepositoryResultFactory.success(usersResponse);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  async createUser(
    payload: CreateUserRequest,
  ): Promise<RepositoryResult<CreateUserResponse>> {
    try {
      // Fake-friendly implementation: we still go through HttpClient so the
      // stack is exercised, but backend can be mocked to echo the user.
      const url = createUserLocalUrl();

      const response = await this.httpClient.post<
        ApiResponse<CreatedUserApiData>,
        CreateUserRequest
      >({
        url,
        body: payload,
        withAuth: true,
      });

      const result =
        ApiResponseHandler.handleResponse<CreatedUserApiData>(response);
      if (result.success) {
        const createUserResponse: CreateUserResponse = {
          code: response.code,
          message: response.message,
          data: result.data ?? null,
        };
        return RepositoryResultFactory.success(createUserResponse);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  getCurrentUserStaffDirectory(): Promise<
    RepositoryResult<StaffDirectoryAttachment | null>
  > {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<CurrentUserDto>>({
          url: getCurrentUserUrl(),
          withAuth: true,
        }),
      mapStaffDirectoryDtoToEntity,
    );
  }

  async getUserById(
    id: string,
  ): Promise<RepositoryResult<UserDetailsResponse>> {
    try {
      const url = getUserByIdLocalUrl(id);

      const response = await this.httpClient.get<ApiResponse<UserData>>({
        url,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<UserData>(response);

      if (result.success && result.data) {
        const user = result.data;

        const normalizedUser: UserData = {
          id: user.id ?? id,
          login: user.login,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          roles: user.roles ?? [],
          branch: user.branch ?? "",
          valid: user.valid ?? true,
          isActivate: user.isActivate ?? true,
          activationDate: user.activationDate,
          expirationDate: user.expirationDate ?? null,
          createdTimestamp: user.createdTimestamp,
          numberPhone: user.numberPhone ?? null,
        };

        const dto: UserDetailsResponse = {
          code: response.code,
          message: response.message,
          data: normalizedUser,
        };

        return RepositoryResultFactory.success(dto);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  async searchLdapUserById(
    id: string,
  ): Promise<RepositoryResult<SearchLdapUserResponse>> {
    try {
      const url = searchLdapUserByIdUrl(id);

      const response = await this.httpClient.get<ApiResponse<LDAPUserData>>({
        url,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<LDAPUserData>(response);

      if (result.success && result.data) {
        const user = result.data;

        const normalizedUser: LDAPUserData = {
          login: user.login,
          matricule: user.matricule,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          numberPhone: user.numberPhone,
          departement: user.departement,
          fonction: user.fonction,
          roles: user.roles,
          branch: user.branch,
          isActivate: user.isActivate,
        };

        const dto: SearchLdapUserResponse = {
          code: response.code,
          message: response.message,
          data: normalizedUser,
        };

        return RepositoryResultFactory.success(dto);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  async searchUserByLogin(
    login: string,
  ): Promise<RepositoryResult<SearchUserByLoginResponse>> {
    try {
      const url = searchUserByLoginUrl(login);

      const response = await this.httpClient.get<ApiResponse<UserByLoginData>>({
        url,
        withAuth: true,
      });

      const result =
        ApiResponseHandler.handleResponse<UserByLoginData>(response);

      if (result.success && result.data) {
        const user = result.data;

        const dto: SearchUserByLoginResponse = {
          code: response.code,
          message: response.message,
          data: {
            ...user,
          },
        };

        return RepositoryResultFactory.success(dto);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response),
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  async updateUser(
    id: string,
    payload: UserData,
  ): Promise<RepositoryResult<UserDetailsResponse>> {
    try {
      const url = updateUserLocalUrl(id);

      const response = await this.httpClient.put<
        ApiResponse<UserData>,
        UserData
      >({
        url,
        body: payload,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<UserData>(response);

      if (result.success && result.data) {
        const updated = result.data;

        const normalizedUser: UserData = {
          id: updated.id ?? id,
          login: updated.login,
          firstName: updated.firstName ?? payload.firstName,
          lastName: updated.lastName ?? payload.lastName,
          email: updated.email ?? payload.email,
          roles: updated.roles ?? [],
          branch: updated.branch ?? "",
          valid: updated.valid ?? true,
          isActivate: updated.isActivate ?? true,
          activationDate: updated.activationDate ?? new Date().toISOString(),
          expirationDate: updated.expirationDate ?? null,
          createdTimestamp:
            updated.createdTimestamp ?? new Date().toISOString(),
        };

        const dto: UserDetailsResponse = {
          code: response.code,
          message: response.message,
          data: normalizedUser,
        };

        return RepositoryResultFactory.success(dto);
      }

      // Fallback fake response if backend is not ready
      const fakeUser: UserData = {
        id,
        login: payload.login,
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        roles: [],
        branch: "",
        valid: true,
        isActivate: true,
        activationDate: new Date().toISOString(),
        expirationDate: null,
        createdTimestamp: new Date().toISOString(),
      };

      const fakeDto: UserDetailsResponse = {
        code: "00",
        message: "Utilisateur mis à jour (fake response).",
        data: fakeUser,
      };

      return RepositoryResultFactory.success(fakeDto);
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  toggleActivateUser(
    id: string,
    isActivate: boolean,
  ): Promise<RepositoryResult<void>> {
    const url = toggleActivateUserLocalUrl(id, isActivate);

    return executeVoidRequest(() =>
      this.httpClient.patch<ApiResponse<void>>({
        url,
        body: null,
        withAuth: true,
      }),
    );
  }

  async assignRoleToUsers(
    payload: AssignRoleToUsersRequest,
  ): Promise<RepositoryResult<AssignRoleToUsersResponse>> {
    try {
      const url = assignRolesToUsersUrl();

      const response = await this.httpClient.patch<
        ApiResponse<AssignRoleToUsersResponse>,
        AssignRoleToUsersRequest
      >({
        url,
        body: payload,
        withAuth: true,
      });

      const result =
        ApiResponseHandler.handleResponse<AssignRoleToUsersResponse>(response);

      if (result.success && result.data) {
        return RepositoryResultFactory.success(result.data);
      }

      // Fallback “fake” si l’API n’est pas encore prête
      const fake: AssignRoleToUsersResponse = {
        code: response.code ?? "00",
        message:
          response.message ??
          "Attribution de rôle enregistrée (fake AssignRoleToUsersResponse).",
        data: null,
      };

      return RepositoryResultFactory.success(fake);
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }

  async revokeRolesFromUser(
    payload: RevokeRolesFromUserRequest,
  ): Promise<RepositoryResult<RevokeRolesFromUserResponse>> {
    try {
      const url = revokeRolesFromUserUrl();

      const response = await this.httpClient.patch<
        ApiResponse<RevokeRolesFromUserResponse>,
        RevokeRolesFromUserRequest
      >({
        url,
        body: payload,
        withAuth: true,
      });

      const result =
        ApiResponseHandler.handleResponse<RevokeRolesFromUserResponse>(
          response,
        );

      if (result.success && result.data) {
        return RepositoryResultFactory.success(result.data);
      }

      // Fallback “fake” si l’API ne renvoie pas de corps exploitable
      const fake: RevokeRolesFromUserResponse = {
        code: response.code ?? "00",
        message:
          response.message ??
          "Révocation des rôles enregistrée (fake RevokeRolesFromUserResponse).",
        data: null,
      };

      return RepositoryResultFactory.success(fake);
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>),
      );
    }
  }
}
